import { useEffect, useRef, useState } from "react";
import { LifeBuoy, MessageCircle, Send, Loader2, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * AdminSupportInbox — admin-only section listing all support threads with
 * unread counts. Clicking a thread opens a drawer to read and reply.
 * Polls threads list every 8s.
 */
export default function AdminSupportInbox() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null); // {user_id, name, email, role}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const scrollRef = useRef(null);
  const sinceRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/admin/support/threads");
      setThreads(data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, 8000);
    return () => clearInterval(id);
  }, []);

  const openThread = async (t) => {
    setSelected(t);
    setLoadingThread(true);
    setMessages([]);
    sinceRef.current = null;
    try {
      const { data } = await api.get(`/admin/support/threads/${t.user_id}/messages`);
      setMessages(data);
      if (data.length > 0) sinceRef.current = data[data.length - 1].created_at;
      loadThreads(); // refresh unread counter
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingThread(false);
    }
  };

  const pollThreadMessages = async () => {
    if (!selected) return;
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/admin/support/threads/${selected.user_id}/messages`, { params });
      if (data.length > 0) {
        setMessages((prev) => {
          const merged = [...prev, ...data];
          const seen = new Set();
          return merged.filter((m) => (seen.has(m.message_id) ? false : (seen.add(m.message_id), true)));
        });
        sinceRef.current = data[data.length - 1].created_at;
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (!selected) return;
    const id = setInterval(pollThreadMessages, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.user_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || !selected || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/support/threads/${selected.user_id}/messages`, { text: body });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setText("");
      loadThreads();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const totalUnread = threads.reduce((sum, t) => sum + (t.unread || 0), 0);

  return (
    <div data-testid="admin-support-inbox">
      <div className="flex items-center gap-2 mb-3">
        <LifeBuoy size={14} className="text-brand-accent" />
        <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// SUPPORT INBOX</div>
        {totalUnread > 0 && (
          <span className="bg-brand-accent text-white text-[10px] font-black uppercase px-2 py-0.5 rounded-full" data-testid="support-inbox-unread-total">
            {totalUnread} NEW
          </span>
        )}
      </div>

      {threads.length === 0 ? (
        <div className="bg-ink-surface border border-ink-border rounded-2xl p-6 text-center text-xs text-ink-textDim" data-testid="support-inbox-empty">
          No support threads yet.
        </div>
      ) : (
        <>
          {/* Waiting-for-agent queue — high-priority pickup */}
          {threads.some((tt) => tt.state === "waiting_agent") && (
            <div className="mb-3 bg-amber-gold/10 border border-amber-gold/50 rounded-2xl overflow-hidden animate-pulse-slow" data-testid="support-waiting-queue">
              <div className="px-4 py-2 text-[10px] uppercase font-black text-amber-gold tracking-widest">
                ⚠ Waiting for agent ({threads.filter((tt) => tt.state === "waiting_agent").length})
              </div>
              <div className="divide-y divide-amber-gold/20">
                {threads.filter((tt) => tt.state === "waiting_agent").map((tt) => (
                  <div key={tt.user_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`support-waiting-${tt.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm truncate">{tt.name}</div>
                      <div className="text-[11px] text-ink-textDim truncate">{tt.last_message}</div>
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post(`/admin/support/threads/${tt.user_id}/claim`);
                          openThread(tt);
                          loadThreads();
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                      className="bg-amber-gold text-black rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest"
                      data-testid={`support-claim-${tt.user_id}`}
                    >
                      Claim
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-ink-surface border border-ink-border rounded-2xl overflow-hidden divide-y divide-ink-border max-h-[400px] overflow-y-auto" data-testid="support-inbox-list">
            {threads.map((t) => (
              <button
                key={t.user_id}
                onClick={() => openThread(t)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-ink-surface2 text-start transition"
                data-testid={`support-thread-${t.user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-ink-surface2 flex items-center justify-center shrink-0">
                  <MessageCircle size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-black text-sm truncate">{t.name}</div>
                    <span className="text-[9px] uppercase font-black text-ink-textDim">{t.role}</span>
                    {t.state && (
                      <span className={`text-[9px] uppercase font-black rounded-full px-1.5 py-0.5 border ${
                        t.state === "waiting_agent" ? "text-amber-gold border-amber-gold/50 bg-amber-gold/10" :
                        t.state === "with_agent" ? "text-brand-accent border-brand-accent/50 bg-brand-accent/10" :
                        t.state === "closed" ? "text-ink-textDim border-ink-border" :
                        "text-electric-teal border-electric-teal/50 bg-electric-teal/10"
                      }`} data-testid={`support-thread-state-${t.user_id}`}>
                        {t.state}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-textDim truncate">
                    {t.last_sender_role === "admin" ? "You: " : ""}{t.last_message}
                  </div>
                </div>
                {t.unread > 0 && (
                  <span className="bg-brand-accent text-white text-[10px] font-black rounded-full px-2 py-0.5 shrink-0" data-testid={`support-thread-unread-${t.user_id}`}>
                    {t.unread}
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Drawer for selected thread */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="support-thread-drawer">
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-[520px] bg-ink-surface border border-ink-border rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-4 py-3 border-b border-ink-border flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-ink-surface2 flex items-center justify-center"><MessageCircle size={14} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{selected.name}</div>
                <div className="text-[10px] uppercase font-black text-ink-textDim">{selected.email} · {selected.role}</div>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full hover:bg-ink-surface2 flex items-center justify-center" data-testid="support-thread-close">
                <X size={14} />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-ink-bg min-h-[240px]" data-testid="support-thread-messages">
              {loadingThread && messages.length === 0 && (
                <div className="text-center text-xs text-ink-textDim py-8"><Loader2 size={14} className="inline animate-spin" /></div>
              )}
              {!loadingThread && messages.length === 0 && (
                <div className="text-center text-xs text-ink-textDim py-8">No messages in this thread yet.</div>
              )}
              {messages.map((m) => {
                const mine = m.sender_role === "admin";
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-brand-accent text-white rounded-br-md" : "bg-ink-surface2 text-white rounded-bl-md border border-ink-border"}`}>
                      {!mine && <div className="text-[10px] uppercase font-black text-ink-textDim mb-0.5">{m.sender_name}</div>}
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-ink-border p-2 flex items-center gap-2 bg-ink-surface">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Reply as Support…"
                className="flex-1 bg-ink-bg border border-ink-border rounded-full px-4 py-2 text-sm outline-none focus:border-brand-accent"
                data-testid="support-thread-input"
              />
              <button
                onClick={send}
                disabled={sending || !text.trim()}
                className="w-9 h-9 rounded-full bg-brand-accent text-white flex items-center justify-center disabled:opacity-40"
                data-testid="support-thread-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
